import { useEffect, useState } from 'react'
import {
  CUTIN_MESSAGES, CUTIN_TEASE_MESSAGE, CUTIN_TIMING, CUTIN_UPGRADE_MS, CUTIN_WINDUP_STEPS,
  cutinRumbleMs, cutinScenario, type CutinTier,
} from '@/lib/ai-result-tier'
import { duckRumble, escalateRumble, playFlash, playRumble, playUpgradeHit, stopRumble, vibrate } from '@/lib/sfx'

/** カットイン内の段階: 溜め（ゴゴゴ）→ 帯＋文言（→ 昇格）→（虹のみ）静止 → 閃光 */
type Phase = 'rumble' | 'band' | 'hold' | 'flash'

/** 昇格中の段階: 発展（……！？）→ 溜め 1〜3（震え → 点滅 → 周りが暗く）。null は昇格していない */
type UpgradeStep = 'tease' | 1 | 2 | 3 | null

const { RUMBLE_MS, BAND_MS, UPGRADE_TEASE_MS, UPGRADE_WINDUP_MS, UPGRADE_AFTER_MS } = CUTIN_TIMING
const WINDUP_STEP_MS = UPGRADE_WINDUP_MS / CUTIN_WINDUP_STEPS

/** 溜めの段階ごとの帯の震え・点滅（index.css の cutin-windup-*） */
const WINDUP_CLASSES: Record<1 | 2 | 3, string> = {
  1: 'cutin-windup-1',
  2: 'cutin-windup-2',
  3: 'cutin-windup-3',
}

const GO_CHARS = [
  { rot: -12, size: 'text-5xl', offset: '-translate-y-3' },
  { rot: 6, size: 'text-6xl', offset: 'translate-y-2' },
  { rot: -4, size: 'text-5xl', offset: '-translate-y-1' },
  { rot: 10, size: 'text-6xl', offset: 'translate-y-3' },
  { rot: -8, size: 'text-5xl', offset: '-translate-y-2' },
]
const GO_STAGGER_MS = 200

/**
 * 振動パターン。ゴゴゴの間は 60ms/40ms で刻む。昇格は、発展で休み → 溜めの段階ごとに細かく速く →
 * ダンッで長く震わせる。navigator.vibrate は呼び直すと前のパターンが止まるため、昇格分まで最初に1本で組む。
 */
function vibrationPattern(upgrades: number): number[] {
  const ticks = (count: number, on: number, off: number) => Array.from({ length: count }, () => [on, off]).flat()
  const perUpgrade = [
    0, UPGRADE_TEASE_MS, // 発展は休み
    ...ticks(WINDUP_STEP_MS / 50, 30, 20),
    ...ticks(WINDUP_STEP_MS / 40, 25, 15),
    ...ticks(WINDUP_STEP_MS / 20, 12, 8),
    200, 100, ...ticks((UPGRADE_AFTER_MS - 300) / 100, 60, 40),
  ]
  return [...ticks((RUMBLE_MS + BAND_MS) / 100, 60, 40), ...Array.from({ length: upgrades }, () => perUpgrade).flat()]
}

/** 段階ごとの配色と揺れの強さ。Tailwind JIT が検出できるよう静的リテラルで持つ。 */
const TIER_STYLES: Record<CutinTier, { go: string; band: string; text: string; shake: string }> = {
  red: {
    go: 'text-red-500 [-webkit-text-stroke:2px_#450a0a]',
    band: 'bg-gradient-to-r from-red-900 via-red-600 to-red-900',
    text: 'text-white',
    shake: 'cutin-shake',
  },
  gold: {
    go: 'text-amber-300 [-webkit-text-stroke:2px_#451a03]',
    band: 'bg-gradient-to-r from-amber-700 via-yellow-300 to-amber-700',
    text: 'text-amber-950',
    shake: 'cutin-shake cutin-shake-strong',
  },
  rainbow: {
    go: 'rainbow-text',
    band: 'cutin-rainbow-band',
    text: 'text-white [text-shadow:0_2px_8px_rgba(0,0,0,.6)]',
    shake: 'cutin-shake cutin-shake-strong',
  },
}

/**
 * 高評価（ai_rating 6 以上）実績があるときのパチンコ風カットイン。
 * 必ず赤から始まり、finalTier まで1段ずつ昇格する。音・揺れ・色も赤の強さから始めて
 * 昇格ごとに強めるので、昇格の瞬間まで結果は分からない。
 * アンマウントでタイマーとゴゴゴは止まる（閃光の音は鳴らし切る。スキップ時は親の skip() が全停止する）。
 */
export default function AiCutin({ tier: finalTier }: { tier: CutinTier }) {
  const [phase, setPhase] = useState<Phase>('rumble')
  const [stage, setStage] = useState(0)
  const [upgradeStep, setUpgradeStep] = useState<UpgradeStep>(null)
  const current = cutinScenario(finalTier)[stage] ?? 'red'
  const style = TIER_STYLES[current]

  useEffect(() => {
    const steps = cutinScenario(finalTier)
    const rumbleEnd = cutinRumbleMs(finalTier)
    playRumble('red', rumbleEnd)
    vibrate(vibrationPattern(steps.length - 1))

    const timers = [
      setTimeout(() => setPhase('band'), RUMBLE_MS),
      // 帯が出た後、1段ずつ昇格する（発展 → 溜め①②③ → ダンッ → 余韻）
      ...steps.slice(1).flatMap((next, i) => {
        const start = RUMBLE_MS + BAND_MS + i * CUTIN_UPGRADE_MS
        const windupAt = start + UPGRADE_TEASE_MS
        return [
          setTimeout(() => {
            setUpgradeStep('tease')
            duckRumble() // 嵐の前の静けさ
          }, start),
          setTimeout(() => {
            setUpgradeStep(1)
            escalateRumble(next, UPGRADE_WINDUP_MS / 1000) // 溜めの間にゴゴゴを次の重さまで膨らませる
          }, windupAt),
          setTimeout(() => setUpgradeStep(2), windupAt + WINDUP_STEP_MS),
          setTimeout(() => setUpgradeStep(3), windupAt + WINDUP_STEP_MS * 2),
          setTimeout(() => {
            setUpgradeStep(null)
            setStage(i + 1)
            playUpgradeHit(next)
          }, windupAt + UPGRADE_WINDUP_MS),
        ]
      }),
      ...(finalTier === 'rainbow' ? [setTimeout(() => setPhase('hold'), rumbleEnd)] : []),
      setTimeout(() => {
        setPhase('flash')
        playFlash()
        vibrate(120)
      }, rumbleEnd + (finalTier === 'rainbow' ? CUTIN_TIMING.HOLD_MS : 0)),
    ]
    return () => {
      timers.forEach(clearTimeout)
      // ゴゴゴだけ止める。閃光の音は次の段階でも鳴らし切り、スキップ時は親の skip() が全停止する
      stopRumble()
    }
  }, [finalTier])

  const shaking = phase === 'rumble' || phase === 'band'
  const bandMotion =
    upgradeStep === 'tease' ? 'cutin-tease' : upgradeStep === null ? '' : WINDUP_CLASSES[upgradeStep]
  // 発展〜溜めの間は次の段階を伏せて「……！？」
  const message = upgradeStep === null ? CUTIN_MESSAGES[current] : CUTIN_TEASE_MESSAGE

  return (
    <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden" aria-hidden>
      {/* ゴゴゴ */}
      <div className={`absolute inset-0 flex items-center justify-center ${shaking ? style.shake : ''}`}>
        <div
          className={`flex items-center gap-1 transition-opacity duration-300 ${
            phase === 'rumble' ? 'opacity-100' : 'opacity-25'
          }`}
        >
          {GO_CHARS.map(({ rot, size, offset }, i) => (
            <span key={i} className={offset}>
              <span
                className={`cutin-slam inline-block font-black ${size}`}
                style={{ animationDelay: `${i * GO_STAGGER_MS}ms`, ['--rot' as string]: `${rot}deg` }}
              >
                {/* 配色（虹は animation を持つ）は叩きつけの animation と別要素に分ける */}
                <span className={style.go}>ゴ</span>
              </span>
            </span>
          ))}
        </div>
      </div>

      {/* 溜めの最終段階：画面の周りを暗くして帯に視線を集める */}
      {upgradeStep === 3 && <div className="cutin-vignette absolute inset-0" />}

      {/* 斜めに差し込む帯＋文言。昇格ごとに key を変えて弾ける演出を再生する */}
      {phase !== 'rumble' && (
        <div className={`absolute inset-x-[-10%] top-1/2 -mt-12 h-24 ${bandMotion}`}>
          <div key={stage} className={`h-full w-full ${stage > 0 ? 'cutin-upgrade' : ''}`}>
            <div
              className={`flex h-full w-full items-center justify-center shadow-2xl ${
                stage === 0 ? 'cutin-band' : 'cutin-band-static'
              } ${style.band}`}
            >
              <p className={`px-4 text-center text-2xl font-black tracking-wider md:text-3xl ${style.text}`}>
                {message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 昇格の閃光（ダンッ） */}
      {stage > 0 && phase !== 'flash' && (
        <div key={`upgrade-${stage}`} className="cutin-upgrade-flash absolute inset-0 bg-white" />
      )}

      {/* 閃光 → 判定完了へ */}
      {phase === 'flash' && <div className="cutin-flash absolute inset-0 bg-white" />}
    </div>
  )
}
